import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle, isTouchDevice } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Guards against re-entering the menu mode measurement, which temporarily
   * mutates `data-menu-style` and triggers events that would call back into it.
   * @type {boolean}
   */
  #evaluatingMenuMode = false;

  /**
   * RAF ID for throttling menu mode re-evaluation on resize
   * @type {number | null}
   */
  #menuModeRafId = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu.
   * The event only ever reports overflow, never its absence, so it is used to
   * react to the very first measurement. Everything after that is driven by
   * #evaluateMenuMode, which measures in both directions.
   */
  #handleOverflowMinimum = () => {
    if (this.#evaluatingMenuMode) return;
    setHeaderMenuStyle();
  };

  /**
   * Custom: this theme never displays the "More" overflow button. When the
   * top-level menu stops fitting, the header switches to the drawer (hamburger)
   * menu instead.
   *
   * The stock implementation could only ever latch in one direction. The
   * `overflowMinimum` event fires from inside `#reflowItems`, which is guarded
   * by `hasOverflow` — so `minimum-reached` gets set when the menu stops
   * fitting but is never cleared when it starts fitting again. Worse, by then
   * the menu is `display: none`, so `<overflow-list>` measures everything as
   * zero and can never discover it would fit. Once the drawer took over it
   * stayed, even at full desktop width.
   *
   * This re-measures from scratch instead of trusting the accumulated state:
   * force the menu back into the layout, clear the flag, ask each list to
   * reflow, then read the resulting overflow count and commit the answer. All
   * of it runs synchronously within one task, so the intermediate state is
   * never painted and there is no flash of the desktop menu.
   */
  #evaluateMenuMode = () => {
    if (this.#evaluatingMenuMode) return;

    const lists = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('overflow-list'));
    if (!lists.length) return;

    this.#evaluatingMenuMode = true;

    try {
      // Below the desktop breakpoint the menu is hidden by media query and can
      // never be measured, and touch devices always get the drawer. Both cases
      // are settled without measuring.
      if (window.innerWidth < 750 || isTouchDevice()) {
        for (const list of lists) list.setAttribute('minimum-reached', '');
        this.dataset.menuStyle = 'drawer';
        return;
      }

      // Put the menu back in the layout so the lists have something to measure.
      this.dataset.menuStyle = 'menu';

      let overflowing = false;

      for (const list of lists) {
        list.removeAttribute('minimum-reached');
        // <overflow-list> exposes its reflow through this event.
        list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
        if (parseInt(list.style.getPropertyValue('--overflow-count'), 10) > 0) {
          overflowing = true;
        }
      }

      // Keep `minimum-reached` consistent with what was actually measured, so
      // any later setHeaderMenuStyle() call agrees rather than fighting this.
      // A split menu renders one list per side; either side overflowing sends
      // the whole header to the drawer.
      for (const list of lists) {
        if (overflowing) list.setAttribute('minimum-reached', '');
        else list.removeAttribute('minimum-reached');
      }

      this.dataset.menuStyle = overflowing ? 'drawer' : 'menu';
    } finally {
      this.#evaluatingMenuMode = false;
    }
  };

  #handleMenuModeResize = () => {
    if (this.#menuModeRafId !== null) return;

    this.#menuModeRafId = requestAnimationFrame(() => {
      this.#menuModeRafId = null;
      this.#evaluateMenuMode();
    });
  };

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Custom: re-measure whether the top-level menu fits. Runs once the page has
    // loaded (by which point <overflow-list> has its shadow styles and is able
    // to measure) and again once webfonts have swapped, since that changes the
    // width of every menu item.
    window.addEventListener('resize', this.#handleMenuModeResize);
    onDocumentLoaded(() => requestAnimationFrame(this.#evaluateMenuMode));
    document.fonts?.ready.then(() => this.#evaluateMenuMode());

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('resize', this.#handleMenuModeResize);
    if (this.#menuModeRafId !== null) {
      cancelAnimationFrame(this.#menuModeRafId);
      this.#menuModeRafId = null;
    }
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
